import AddIcon from '@mui/icons-material/Add';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { Box, Card, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { tr } from '../lib/i18n';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { fetchAllPages } from '../api/client';
import type { PropertyRow } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { Button } from '../components/button';
import { ErrorState } from '../components/common';
import { PageActions } from '../components/page-actions';
import { DataGrid, type GridColDef, type GridFilterModel } from '../components/data-grid';
import { PropertyStatusChip, SourceChip } from '../components/org';
import { ActiveFormFilters, filtersQuery, FormFiltersButton, useSavedFilters } from '../components/FormFilters';
import { apiLabel, fmtDay, fmtNum, money, PROPERTY_STATUS, SOURCE } from '../lib/labels';

export function PropertiesPage() {
  const { can, me } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'pending' ? 'pending' : 'all';
  const [filters, setFilters] = useSavedFilters('OFFER');
  const list = useQuery({ queryKey: ['properties', filters], queryFn: () => fetchAllPages<PropertyRow>('/properties', filtersQuery(filters)), placeholderData: keepPreviousData });
  const all = list.data ?? [];
  const pendingCount = all.filter((r) => r.status === 'PENDING_REVIEW').length;
  const rows = view === 'pending' ? all.filter((r) => r.status === 'PENDING_REVIEW') : all;
  const setView = (v: string) => setParams((p) => { const n = new URLSearchParams(p); if (v === 'pending') n.set('view', 'pending'); else n.delete('view'); return n; }, { replace: true });

  const columns = useMemo<GridColDef<PropertyRow>[]>(() => [
    { field: 'ref_no', headerName: tr('المرجع', 'Reference'), width: 140, renderCell: ({ value }) => <Box dir="ltr" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 13, color: 'text.secondary', fontWeight: 500 }}>{value}</Box> },
    {
      field: 'property',
      headerName: tr('العقار', 'Property'),
      flex: 1.3,
      minWidth: 200,
      valueGetter: (_, r) => `${r.request_type_name} ${r.property_type_name} ${r.category_name ?? ''}`,
      renderCell: ({ row: r }) => (
        <Box sx={{ minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', lineHeight: 1.35 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="body2" fontWeight={500} component="span">{apiLabel(r.request_type_name)} · {apiLabel(r.property_type_name)}</Typography>
            {r.media_count > 0 && <Tooltip title={`${r.media_count} ${tr('صورة', 'photos')}`}><PhotoLibraryIcon fontSize="inherit" color="action" /></Tooltip>}
          </Stack>
          <Typography variant="caption" color="text.secondary">{apiLabel(r.category_name)}{r.rooms != null ? ` · ${r.rooms} ${tr('غرف', 'rooms')}` : ''}</Typography>
        </Box>
      ),
    },
    {
      field: 'location',
      headerName: tr('الموقع', 'Location'),
      flex: 1,
      minWidth: 160,
      valueGetter: (_, r) => [r.governorate_name, r.area_name, r.neighborhood_name].filter(Boolean).join(' / '),
    },
    {
      field: 'price',
      headerName: tr('السعر', 'Price'),
      width: 140,
      type: 'number',
      valueGetter: (_, r) => (r.price == null || r.price === '' ? null : Number(r.price)),
      valueFormatter: (v, r) => money(v as number | null, r.currency),
    },
    {
      field: 'area',
      headerName: tr('المساحة', 'Area'),
      width: 120,
      valueGetter: (_, r) => r.area ?? r.building_area ?? r.land_area,
      valueFormatter: (v) => (v == null || v === '' ? '—' : `${fmtNum(v as string)} m²`),
    },
    {
      field: 'owner_name',
      headerName: tr('المالك', 'Owner'),
      width: 160,
      valueGetter: (_, r) => `${r.owner_name ?? ''} ${r.owner_phone ?? ''}`,
      renderCell: ({ row: r }) => (
        <Box sx={{ minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', lineHeight: 1.35 }}>
          {r.owner_name ?? '—'}
          <Typography variant="caption" display="block" color="text.secondary"><bdi>{r.owner_phone}</bdi></Typography>
        </Box>
      ),
    },
    {
      field: 'source',
      headerName: tr('المصدر', 'Source'),
      width: 130,
      type: 'singleSelect',
      valueOptions: Object.entries(SOURCE).map(([value, label]) => ({ value, label })),
      renderCell: ({ row: r }) => <SourceChip source={r.source} />,
    },
    {
      field: 'status',
      headerName: tr('الحالة', 'Status'),
      width: 150,
      type: 'singleSelect',
      valueOptions: Object.entries(PROPERTY_STATUS).map(([value, label]) => ({ value, label })),
      renderCell: ({ row: r }) => <PropertyStatusChip status={r.status} />,
    },
    {
      field: 'created_by_name',
      headerName: tr('أدخله', 'Created by'),
      width: 140,
      valueGetter: (_, r) => (r.created_by === me?.user.id ? tr('أنا', 'Me') : r.created_by_name ?? tr('العميل', 'Customer')),
    },
    {
      field: 'assigneeLabel',
      headerName: tr('المسؤول', 'Assignee'),
      width: 140,
      valueGetter: (_, r) => (r.assigned_to === me?.user.id ? tr('أنا', 'Me') : r.assigned_to_name ?? tr('غير معيّن', 'Unassigned')),
    },
    { field: 'created_at', headerName: tr('التاريخ', 'Date'), width: 140, valueFormatter: (v) => fmtDay(v as string) },
  ], [me]);

  const assignedTo = params.get('assignedTo') ?? '';
  const initialFilter = useMemo<GridFilterModel>(() => {
    if (assignedTo === 'unassigned') return { items: [{ id: 1, field: 'assigneeLabel', operator: 'equals', value: tr('غير معيّن', 'Unassigned') }] };
    if (assignedTo === 'me') return { items: [{ id: 1, field: 'assigneeLabel', operator: 'equals', value: tr('أنا', 'Me') }] };
    return { items: [] };
  }, [assignedTo]);

  return (
    <>
      <PageActions>
        <FormFiltersButton kind="OFFER" value={filters} onChange={setFilters} />
        {can('form.submit') && can('properties.create') && <Button variant="contained" startIcon={<AddIcon />} component={RouterLink} to="/new?kind=OFFER">{tr('إدخال جديد', 'New entry')}</Button>}
      </PageActions>
      <Card sx={{ flex: 1, minHeight: 420, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Tabs value={view} onChange={(_, v) => setView(v)} sx={{ px: 1, borderBottom: 1, borderColor: 'divider', minHeight: 44, '& .MuiTab-root': { minHeight: 44 } }}>
          <Tab value="all" label={tr(`الكل (${all.length})`, `All (${all.length})`)} />
          <Tab
            value="pending"
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>{tr('بانتظار الاعتماد', 'Waiting for approval')}</span>
                {pendingCount > 0 && <Box component="span" sx={{ minWidth: 22, height: 20, px: 0.75, borderRadius: 999, bgcolor: 'warning.main', color: 'warning.contrastText', fontSize: 12, fontWeight: 700, display: 'inline-grid', placeItems: 'center' }}>{pendingCount}</Box>}
              </Stack>
            }
          />
        </Tabs>
        {view === 'pending' && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
            {pendingCount
              ? tr('عروض وصلت من الرابط العام. لا تدخل المطابقة حتى تعتمدها — افتح العرض واضغط «اعتماد» أو «رفض».', 'Offers from the public link. They are not matched until you approve them — open one and choose Approve or Reject.')
              : tr('لا توجد عروض بانتظار الاعتماد.', 'No offers waiting for approval.')}
          </Typography>
        )}
        <ActiveFormFilters kind="OFFER" value={filters} onChange={setFilters} />
        {list.isError ? <Box sx={{ p: 2 }}><ErrorState error={list.error} onRetry={() => list.refetch()} /></Box> : (
          <Box sx={{ flex: 1, minHeight: 0 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={list.isLoading}
            onRowClick={({ row }) => nav(`/properties/${row.id}`)}
            sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
            initialState={{ filter: { filterModel: initialFilter } }}
            rowHeight={60}
            quickFilter={params.get('q')}
          />
          </Box>
        )}
      </Card>
    </>
  );
}
